import React, { useState } from 'react';
import { Box, Button, Flex, Text, TextInput, useToasts } from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import EnvelopeIcon from '../../assets/EnvelopeIcon';
import { VasError, VasErrorBody } from '../../common/errors/VasError';
import {
  useGetReferringRPId,
  useGetSource,
  useGetUtmSource,
} from '../../common/hooks/useQueryParams';
import { useRegenerateVerificationCode, useVerifyRbcGicApplicationV2 } from '../../data/rbcGic';
import { RbcGicEvents, RbcGicFormTypeV2 } from './types';

interface VerificationFormProps {
  setFormState: React.Dispatch<React.SetStateAction<RbcGicFormTypeV2>>;
  formState: RbcGicFormTypeV2;
  goNext: () => void;
  goBack: () => void;
}
const VerificationForm: React.FC<VerificationFormProps> = ({
  goNext,
  goBack,
  formState,
  setFormState,
}) => {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState<string>();
  const [isCodeValid, setIsCodeValid] = useState(true);
  const { verifyRbcGicApplicationV2, isVerifyingApplication } = useVerifyRbcGicApplicationV2();
  const { regenerateVerificationCode } = useRegenerateVerificationCode();
  const toasts = useToasts();
  const referrerId = useGetReferringRPId();
  const utmSource = useGetUtmSource();
  const source = useGetSource();
  const { trackEvent } = useEventTracker();
  const showInvalidCodeErrorMsg = !isCodeValid;
  const isInputCodeValid =
    verificationCode === undefined ||
    (verificationCode && /^\d+$/.test(verificationCode) && verificationCode.length === 6);
  const verifyBtnDisabled =
    isVerifyingApplication || !isInputCodeValid || verificationCode === undefined;

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      <Flex direction="column" align="center" gap={4} py={24} px={24}>
        <Box>
          <EnvelopeIcon />
        </Box>
        <Box pt={4}>
          <Text intent="primary" variant="headlineL" align="center">
            {t('rbcv2.verification.title')}
          </Text>
        </Box>
        <Box>
          <Text intent="secondary" variant="bodyS" align="center">
            {t('rbcv2.verification.bodyLine1')}
          </Text>
          <Text intent="secondary" variant="bodyS" align="center">
            {t('rbcv2.verification.bodyLine2')}
          </Text>
          <Text intent="secondary" variant="bodyS" align="center">
            {t('rbcv2.verification.bodyLine3')}
          </Text>
        </Box>
        <TextInput
          intent="secondary"
          label=" "
          placeholder={t('rbcv2.verification.verificationCodePlaceholder')}
          size="md"
          type="text"
          value={verificationCode ?? ''}
          invalid={!isInputCodeValid}
          onChange={(value: string) => {
            setIsCodeValid(true);
            setVerificationCode(value);
          }}
        />
        {showInvalidCodeErrorMsg && (
          <Box>
            <div style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: '14px', color: '#EF1A1A' }}>
                  {t('rbcv2.errors.invalidCodeLine1')} <br />
                  {t('rbcv2.errors.invalidCodeLine2')}
                </span>
              </div>
            </div>
          </Box>
        )}
        <Flex direction="column" gap={3}>
          <Button
            emphasis="transparent"
            intent="primary"
            size="sm"
            type="button"
            width="hug"
            onClick={async () => {
              regenerateVerificationCode(
                formState.id ?? '',
                () => {
                  toasts.positive(t('rbcv2.verification.verificationCodeResent'));
                },
                error => {
                  console.error('error: ', error);
                  const toastError = error as Error;
                  let errorMessage = toastError.message ?? t('rbcv2.errors.errorResendCode');
                  if (error instanceof VasError) {
                    const vasError: VasError = error;
                    const errorBody: VasErrorBody = vasError.errorBody;
                    errorMessage = vasError.message;
                    if (vasError.errorBody.statusCode === 409) {
                      errorMessage =
                        errorBody.message instanceof Array<string>
                          ? errorBody.message.join(',')
                          : errorBody.message;
                    }
                  }
                  setIsCodeValid(false);
                  toasts.negative(new Error(errorMessage));
                },
              );
            }}
          >
            {t('rbcv2.verification.resendCode')}
          </Button>
          <Button
            intent="primary"
            size="sm"
            width="fill"
            type="submit"
            disabled={verifyBtnDisabled}
            onClick={async () => {
              setIsCodeValid(true);
              verifyRbcGicApplicationV2(
                { id: formState.id ?? '', verificationCode: verificationCode ?? '' },
                () => {
                  formState.verificationCode = verificationCode;
                  setFormState(formState);
                  toasts.positive(t('rbcv2.verification.successfulVerification'));
                  trackEvent({
                    event: RbcGicEvents.VerifiedForm,
                    data: { rp_id: referrerId, utm_source: utmSource, source: source },
                  });
                  goNext();
                },
                error => {
                  console.error('error: ', error);
                  let errorMessage: string = t('rbcv2.errors.errorVerifyApplication');
                  if (error instanceof VasError) {
                    const vasError: VasError = error;
                    const errorBody: VasErrorBody = vasError.errorBody;
                    errorMessage = vasError.message;
                    if (vasError.errorBody.statusCode === 409) {
                      errorMessage =
                        errorBody.message instanceof Array<string>
                          ? errorBody.message.join(',')
                          : errorBody.message;
                    }
                  }
                  setIsCodeValid(false);
                  toasts.negative(new Error(errorMessage));
                },
              );
            }}
          >
            {t('rbcv2.verification.verify')}
          </Button>
        </Flex>
      </Flex>
      <Box pl={16}>
        <Button
          emphasis="transparent"
          intent="primary"
          onClick={goBack}
          size="md"
          type="button"
          width="hug"
        >
          {t('common.goBack')}
        </Button>
      </Box>
    </div>
  );
};

export default VerificationForm;
